import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import {
  ACCESS_GROUP_CATEGORIES,
  AccessGroupCategory,
  AccessGroupCategoryMeta,
  AccessGroupClientOption,
  AccessGroupListItem,
  AccessGroupUserOption,
  SaveAccessGroupPayload,
  accessGroupCategoryLabel,
  createEmptyAccessGroupForm,
  mapAccessGroupClientOption,
  mapAccessGroupList,
  mapAccessGroupMembers,
  mapAccessGroupOptionPage,
  mapAccessGroupUserOption,
} from '../../../../core/models/system-settings/access-group.model';
import {
  FreeParkingSubscriptionOption,
  mapFreeParkingSubscriptionList,
} from '../../../../core/models/system-settings/free-quota.model';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { PssConfirmModalComponent, PssConfirmModalVariant } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';

type EditorMode = 'list' | 'create' | 'edit';
type ClientScopeMode = 'all' | 'specific';

interface CategorySection {
  meta: AccessGroupCategoryMeta;
  rows: AccessGroupListItem[];
}

const PICKER_PAGE_SIZE = 20;
const PREVIEW_LIMIT = 12;

@Component({
  selector: 'app-access-groups',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent, PssConfirmModalComponent],
  templateUrl: './access-groups.component.html',
  styleUrl: './access-groups.component.css',
})
export class AccessGroupsComponent implements OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiSystemSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly activityEntityType = AdminActivityEntityTypes.AccessGroup;
  readonly categories = ACCESS_GROUP_CATEGORIES;
  readonly categoryLabel = accessGroupCategoryLabel;
  readonly previewLimit = PREVIEW_LIMIT;

  readonly wizardSteps = [
    { id: 1, title: 'หมวดหมู่กลุ่ม', hint: 'เลือกหมวดหมู่และตั้งชื่อกลุ่ม', icon: 'category' },
    { id: 2, title: 'บริษัทผู้เช่า', hint: 'กำหนดว่ากลุ่มนี้ดูแลบริษัทไหนบ้าง', icon: 'business_center' },
    { id: 3, title: 'สมาชิก', hint: 'ค้นหาและเลือกผู้ใช้ที่อยู่ในกลุ่ม', icon: 'group' },
    { id: 4, title: 'สิทธิ์', hint: 'สมาชิกในกลุ่มทำอะไรได้บ้าง', icon: 'verified_user' },
    { id: 5, title: 'Subscription', hint: 'แพ็กเกจจอดฟรีที่กลุ่มใช้ออกบัตรได้', icon: 'card_membership' },
    { id: 6, title: 'ตรวจสอบ', hint: 'ตรวจสรุปแล้วบันทึก', icon: 'fact_check' },
  ] as const;

  /* ---------- List ---------- */
  items = signal<AccessGroupListItem[]>([]);
  isLoading = signal(false);
  listSearch = signal('');
  listCategory = signal<'all' | AccessGroupCategory>('all');
  listStatus = signal<'all' | 'active' | 'inactive'>('all');

  filteredList = computed(() => {
    const q = this.listSearch().trim().toLowerCase();
    const status = this.listStatus();
    return this.items().filter((r) => {
      if (status === 'active' && !r.active) return false;
      if (status === 'inactive' && r.active) return false;
      if (!q) return true;
      return `${r.name ?? ''} ${accessGroupCategoryLabel(r.groupType)} #${r.id}`.toLowerCase().includes(q);
    });
  });

  categorySections = computed<CategorySection[]>(() => {
    const cat = this.listCategory();
    const rows = this.filteredList();
    return this.categories
      .filter((c) => cat === 'all' || c.value === cat)
      .map((meta) => ({ meta, rows: rows.filter((r) => (r.groupType || 'building') === meta.value) }));
  });

  listStats = computed(() => {
    const all = this.items();
    return {
      total: all.length,
      active: all.filter((g) => g.active).length,
      building: all.filter((g) => (g.groupType || 'building') === 'building').length,
      tenant: all.filter((g) => g.groupType === 'tenant').length,
      members: all.reduce((sum, g) => sum + (g.userCount || 0), 0),
    };
  });

  /* ---------- Editor ---------- */
  editorMode = signal<EditorMode>('list');
  wizardStep = signal(1);
  isSaving = signal(false);
  isMembersLoading = signal(false);
  form: SaveAccessGroupPayload = createEmptyAccessGroupForm(0);
  clientScopeMode: ClientScopeMode = 'all';

  subscriptions = signal<FreeParkingSubscriptionOption[]>([]);
  subSearch = '';

  /* user picker */
  userSearch = '';
  userHideAssigned = true;
  userPage = signal(1);
  userOptions = signal<AccessGroupUserOption[]>([]);
  userTotal = signal(0);
  userTotalPages = signal(0);
  isUserLoading = signal(false);
  selectedUserFilter = '';
  private readonly userCache = new Map<number, AccessGroupUserOption>();

  /* client picker */
  clientSearch = '';
  clientPage = signal(1);
  clientOptions = signal<AccessGroupClientOption[]>([]);
  clientTotal = signal(0);
  clientTotalPages = signal(0);
  isClientLoading = signal(false);
  selectedClientFilter = '';
  private readonly clientCache = new Map<number, AccessGroupClientOption>();

  /* ---------- History / confirm ---------- */
  showActivityHistory = false;
  historyRow = signal<AccessGroupListItem | null>(null);

  showConfirm = false;
  confirmTitle = 'ยืนยันการดำเนินการ?';
  confirmMessage = '';
  confirmHighlight = '';
  confirmMessageSuffix = 'กด ใช่ เพื่อยืนยัน หรือ ไม่ เพื่อยกเลิก';
  confirmVariant: PssConfirmModalVariant = 'warning';
  confirmIcon = 'help';
  confirmLoading = false;
  private pendingConfirmAction: (() => void) | null = null;

  private userSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private clientSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private userRequestSeq = 0;
  private clientRequestSeq = 0;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      untracked(() => {
        if (this.isEditing) this.backToList();
        if (site?.id) this.loadTable();
        else this.items.set([]);
      });
    });
  }

  ngOnDestroy(): void {
    if (this.userSearchTimer) clearTimeout(this.userSearchTimer);
    if (this.clientSearchTimer) clearTimeout(this.clientSearchTimer);
  }

  /* ---------- Getters ---------- */

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get isCreate(): boolean {
    return this.editorMode() === 'create';
  }

  get isEditing(): boolean {
    return this.editorMode() === 'create' || this.editorMode() === 'edit';
  }

  get isLocked(): boolean {
    return !this.canUpdate || this.isSaving();
  }

  get currentStepMeta() {
    return this.wizardSteps.find((s) => s.id === this.wizardStep()) ?? this.wizardSteps[0];
  }

  get formCategory(): AccessGroupCategoryMeta {
    return this.categories.find((c) => c.value === this.form.groupType) ?? this.categories[0];
  }

  get historyEntityId(): number {
    const fromRow = this.historyRow()?.id ?? 0;
    if (fromRow > 0) return fromRow;
    return this.form.id && this.form.id > 0 ? this.form.id : 0;
  }

  get historySubtitle(): string {
    return this.historyRow()?.name || this.form.name || '';
  }

  get selectedUsers(): AccessGroupUserOption[] {
    const q = this.selectedUserFilter.trim().toLowerCase();
    return this.form.resUserIds
      .map((id) => this.userCache.get(id) ?? this.placeholderUser(id))
      .filter((u) => !q || `${u.displayName} ${u.login} ${u.id}`.toLowerCase().includes(q))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  get selectedClients(): AccessGroupClientOption[] {
    const q = this.selectedClientFilter.trim().toLowerCase();
    return this.form.clientIds
      .map((id) => this.clientCache.get(id) ?? this.placeholderClient(id))
      .filter((c) => !q || `${c.name} ${c.taxId} ${c.id}`.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get allUsersOnPageSelected(): boolean {
    const selectable = this.userOptions().filter((u) => !this.isUserBlocked(u));
    return selectable.length > 0 && selectable.every((u) => this.isUserSelected(u.id));
  }

  get allClientsOnPageSelected(): boolean {
    const rows = this.clientOptions();
    return rows.length > 0 && rows.every((c) => this.isClientSelected(c.id));
  }

  get filteredSubscriptions(): FreeParkingSubscriptionOption[] {
    const q = this.subSearch.trim().toLowerCase();
    const all = this.subscriptions();
    if (!q) return all;
    return all.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        String(s.id).includes(q) ||
        (s.vehicleTypeName ?? '').toLowerCase().includes(q),
    );
  }

  get selectedSubscriptions(): FreeParkingSubscriptionOption[] {
    const map = new Map(this.subscriptions().map((s) => [s.id, s]));
    return this.form.subscriptionIds
      .map((id) => map.get(id) ?? { id, name: `Sub #${id}`, vehicleTypeId: null, vehicleTypeName: '' })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get reviewUsers(): AccessGroupUserOption[] {
    return this.form.resUserIds
      .map((id) => this.userCache.get(id) ?? this.placeholderUser(id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  get reviewClients(): AccessGroupClientOption[] {
    return this.form.clientIds
      .map((id) => this.clientCache.get(id) ?? this.placeholderClient(id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  stepDone(stepId: number): boolean {
    return stepId < this.wizardStep() && this.validateStep(stepId, false);
  }

  categoryCount(value: 'all' | AccessGroupCategory): number {
    const stats = this.listStats();
    return value === 'all' ? stats.total : value === 'tenant' ? stats.tenant : stats.building;
  }

  trackRow(_: number, row: AccessGroupListItem): number {
    return row.id;
  }

  trackById(_: number, row: { id: number }): number {
    return row.id;
  }

  /* ---------- List actions ---------- */

  onRefresh(): void {
    this.loadTable();
  }

  startCreate(category: AccessGroupCategory = 'building'): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์สร้างกลุ่ม');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจากแถบด้านข้างก่อน');
      return;
    }
    this.form = createEmptyAccessGroupForm(this.selectedSiteId);
    this.resetEditorState();
    this.applyCategory(category, true);
    this.editorMode.set('create');
    this.loadSubscriptions(this.selectedSiteId);
  }

  openGroup(row: AccessGroupListItem): void {
    this.form = {
      userId: 0,
      menuId: 0,
      id: row.id,
      name: row.name ?? '',
      groupType: row.groupType || 'building',
      siteId: row.siteId,
      active: row.active,
      canManageQuota: row.canManageQuota,
      canManageFreeCards: row.canManageFreeCards,
      canDeleteCardReturnQuota: row.canDeleteCardReturnQuota,
      resUserIds: [...(row.resUserIds ?? [])],
      clientIds: [...(row.clientIds ?? [])],
      subscriptionIds: [...(row.subscriptionIds ?? [])],
    };
    this.resetEditorState();
    this.clientScopeMode = this.form.clientIds.length ? 'specific' : 'all';
    this.editorMode.set('edit');
    this.loadSubscriptions(row.siteId);
    this.loadMembers(row.id);
  }

  backToList(): void {
    this.editorMode.set('list');
    this.resetEditorState();
    this.form = createEmptyAccessGroupForm(this.selectedSiteId);
  }

  openHistory(row: AccessGroupListItem, event?: Event): void {
    event?.stopPropagation();
    if (!row?.id || row.id <= 0) {
      this.toast.error('ยังไม่มีประวัติสำหรับกลุ่มนี้');
      return;
    }
    this.historyRow.set(row);
    this.showActivityHistory = true;
  }

  openHistoryForCurrentGroup(): void {
    if (!this.form.id || this.form.id <= 0) {
      this.toast.error('บันทึกกลุ่มก่อนจึงจะดูประวัติได้');
      return;
    }
    this.historyRow.set(null);
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
    this.historyRow.set(null);
  }

  /* ---------- Wizard navigation ---------- */

  goStep(step: number): void {
    if (step < 1 || step > this.wizardSteps.length || step === this.wizardStep()) return;
    if (step > this.wizardStep()) {
      for (let s = this.wizardStep(); s < step; s++) {
        if (!this.validateStep(s, true)) {
          this.setStep(s);
          return;
        }
      }
    }
    this.setStep(step);
  }

  nextStep(): void {
    if (!this.validateStep(this.wizardStep(), true)) return;
    if (this.wizardStep() < this.wizardSteps.length) this.setStep(this.wizardStep() + 1);
  }

  prevStep(): void {
    if (this.wizardStep() > 1) this.setStep(this.wizardStep() - 1);
  }

  /* ---------- Step 1: category ---------- */

  selectCategory(value: AccessGroupCategory): void {
    if (this.isLocked || this.form.groupType === value) return;
    this.applyCategory(value, this.isCreate);
  }

  /* ---------- Step 2: tenant companies ---------- */

  setClientScopeMode(mode: ClientScopeMode): void {
    if (this.isLocked) return;
    this.clientScopeMode = mode;
    if (mode === 'specific' && !this.clientOptions().length) this.loadClientOptions();
  }

  onClientSearchChange(): void {
    if (this.clientSearchTimer) clearTimeout(this.clientSearchTimer);
    this.clientSearchTimer = setTimeout(() => {
      this.clientSearchTimer = null;
      this.clientPage.set(1);
      this.loadClientOptions();
    }, 300);
  }

  setClientPage(page: number): void {
    if (page < 1 || page > this.clientTotalPages() || page === this.clientPage()) return;
    this.clientPage.set(page);
    this.loadClientOptions();
  }

  isClientSelected(id: number): boolean {
    return this.form.clientIds.includes(id);
  }

  toggleClient(opt: AccessGroupClientOption): void {
    if (this.isLocked) return;
    this.clientCache.set(opt.id, opt);
    this.form.clientIds = this.isClientSelected(opt.id)
      ? this.form.clientIds.filter((x) => x !== opt.id)
      : [...this.form.clientIds, opt.id];
  }

  toggleAllClientsOnPage(): void {
    if (this.isLocked) return;
    const rows = this.clientOptions();
    if (this.allClientsOnPageSelected) {
      const pageIds = new Set(rows.map((c) => c.id));
      this.form.clientIds = this.form.clientIds.filter((id) => !pageIds.has(id));
      return;
    }
    const set = new Set(this.form.clientIds);
    for (const c of rows) {
      this.clientCache.set(c.id, c);
      set.add(c.id);
    }
    this.form.clientIds = Array.from(set);
  }

  removeClient(id: number): void {
    if (this.isLocked) return;
    this.form.clientIds = this.form.clientIds.filter((x) => x !== id);
  }

  clearClients(): void {
    if (this.isLocked) return;
    this.form.clientIds = [];
  }

  /* ---------- Step 3: users ---------- */

  onUserSearchChange(): void {
    if (this.userSearchTimer) clearTimeout(this.userSearchTimer);
    this.userSearchTimer = setTimeout(() => {
      this.userSearchTimer = null;
      this.userPage.set(1);
      this.loadUserOptions();
    }, 300);
  }

  onUserHideAssignedChange(): void {
    this.userPage.set(1);
    this.loadUserOptions();
  }

  setUserPage(page: number): void {
    if (page < 1 || page > this.userTotalPages() || page === this.userPage()) return;
    this.userPage.set(page);
    this.loadUserOptions();
  }

  isUserSelected(id: number): boolean {
    return this.form.resUserIds.includes(id);
  }

  /** already a member of another group at this site (active or not) */
  isUserBlocked(u: AccessGroupUserOption): boolean {
    return !!u.groupId && u.groupId !== (this.form.id ?? 0) && !this.isUserSelected(u.id);
  }

  toggleUser(opt: AccessGroupUserOption): void {
    if (this.isLocked) return;
    if (this.isUserBlocked(opt)) {
      this.toast.warn(`${opt.displayName} อยู่ในกลุ่ม "${opt.groupName || opt.groupId}" ของลานนี้แล้ว`);
      return;
    }
    this.userCache.set(opt.id, opt);
    this.form.resUserIds = this.isUserSelected(opt.id)
      ? this.form.resUserIds.filter((x) => x !== opt.id)
      : [...this.form.resUserIds, opt.id];
  }

  toggleAllUsersOnPage(): void {
    if (this.isLocked) return;
    const selectable = this.userOptions().filter((u) => !this.isUserBlocked(u));
    if (this.allUsersOnPageSelected) {
      const pageIds = new Set(selectable.map((u) => u.id));
      this.form.resUserIds = this.form.resUserIds.filter((id) => !pageIds.has(id));
      return;
    }
    const set = new Set(this.form.resUserIds);
    for (const u of selectable) {
      this.userCache.set(u.id, u);
      set.add(u.id);
    }
    this.form.resUserIds = Array.from(set);
  }

  removeUser(id: number): void {
    if (this.isLocked) return;
    this.form.resUserIds = this.form.resUserIds.filter((x) => x !== id);
  }

  clearUsers(): void {
    if (this.isLocked) return;
    this.form.resUserIds = [];
  }

  /* ---------- Step 5: subscriptions ---------- */

  isSubSelected(id: number): boolean {
    return this.form.subscriptionIds.includes(id);
  }

  toggleSub(id: number): void {
    if (this.isLocked) return;
    this.form.subscriptionIds = this.isSubSelected(id)
      ? this.form.subscriptionIds.filter((x) => x !== id)
      : [...this.form.subscriptionIds, id];
  }

  selectAllVisibleSubs(): void {
    if (this.isLocked) return;
    const set = new Set(this.form.subscriptionIds);
    for (const s of this.filteredSubscriptions) set.add(s.id);
    this.form.subscriptionIds = Array.from(set);
  }

  /* ---------- Save ---------- */

  onSave(): void {
    if (!this.validateAll(true)) return;

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload: SaveAccessGroupPayload = {
      ...this.form,
      userId,
      menuId,
      id: this.form.id && this.form.id > 0 ? this.form.id : null,
      name: this.form.name?.trim(),
      clientIds: this.clientScopeMode === 'all' ? [] : [...this.form.clientIds],
      resUserIds: [...this.form.resUserIds],
      subscriptionIds: [...this.form.subscriptionIds],
    };

    this.askConfirm(
      {
        title: payload.id ? 'ยืนยันแก้ไขกลุ่มสิทธิ์?' : 'ยืนยันสร้างกลุ่มสิทธิ์?',
        message: `${accessGroupCategoryLabel(payload.groupType)} · สมาชิก ${payload.resUserIds.length} คน · ` +
          (payload.clientIds.length ? `บริษัทผู้เช่า ${payload.clientIds.length} บริษัท` : 'ทุกบริษัทผู้เช่าในลาน'),
        highlight: `${payload.name} · ${this.selectedSiteName}`,
        variant: 'warning',
        icon: 'save',
      },
      () => {
        this.isSaving.set(true);
        this.confirmLoading = true;
        this.api.saveAccessGroup(payload).subscribe({
          next: (res) => {
            console.log('saveAccessGroup', res);
            if (res?.isSuccess ?? res?.IsSuccess) {
              this.toast.success(res?.message ?? res?.Message ?? 'บันทึกสำเร็จ');
              this.finishSave();
              this.backToList();
              this.loadTable();
            } else {
              this.toast.error(res?.message ?? res?.Message ?? 'บันทึกไม่สำเร็จ');
              this.finishSave();
            }
          },
          error: () => {
            this.toast.error('เกิดข้อผิดพลาดขณะบันทึก');
            this.finishSave();
          },
        });
      },
    );
  }

  /* ---------- Confirm ---------- */

  askConfirm(
    opts: { title: string; message: string; highlight?: string; variant?: PssConfirmModalVariant; icon?: string },
    action: () => void,
  ): void {
    this.confirmTitle = opts.title;
    this.confirmMessage = opts.message;
    this.confirmHighlight = opts.highlight ?? '';
    this.confirmVariant = opts.variant ?? 'warning';
    this.confirmIcon = opts.icon ?? 'help';
    this.confirmLoading = false;
    this.pendingConfirmAction = action;
    this.showConfirm = true;
  }

  onConfirmYes(): void {
    const action = this.pendingConfirmAction;
    this.pendingConfirmAction = null;
    action?.();
  }

  onConfirmNo(): void {
    if (this.confirmLoading) return;
    this.showConfirm = false;
    this.pendingConfirmAction = null;
  }

  /* ---------- Private ---------- */

  private setStep(step: number): void {
    this.wizardStep.set(step);
    if (step === 2 && this.clientScopeMode === 'specific' && !this.clientOptions().length) this.loadClientOptions();
    if (step === 3 && !this.userOptions().length) this.loadUserOptions();
  }

  private applyCategory(value: AccessGroupCategory, applyPreset: boolean): void {
    this.form.groupType = value;
    if (!applyPreset) return;
    if (value === 'tenant') {
      this.form.canManageQuota = false;
      this.form.canManageFreeCards = true;
      this.form.canDeleteCardReturnQuota = false;
      this.clientScopeMode = 'specific';
    } else {
      this.form.canManageQuota = true;
      this.form.canManageFreeCards = true;
      this.form.canDeleteCardReturnQuota = false;
      this.clientScopeMode = 'all';
    }
  }

  private resetEditorState(): void {
    this.wizardStep.set(1);
    this.clientScopeMode = 'all';
    this.subSearch = '';
    this.userSearch = '';
    this.userHideAssigned = true;
    this.userPage.set(1);
    this.userOptions.set([]);
    this.userTotal.set(0);
    this.userTotalPages.set(0);
    this.selectedUserFilter = '';
    this.clientSearch = '';
    this.clientPage.set(1);
    this.clientOptions.set([]);
    this.clientTotal.set(0);
    this.clientTotalPages.set(0);
    this.selectedClientFilter = '';
    this.userRequestSeq++;
    this.clientRequestSeq++;
  }

  private finishSave(): void {
    this.isSaving.set(false);
    this.confirmLoading = false;
    this.showConfirm = false;
  }

  private placeholderUser(id: number): AccessGroupUserOption {
    return { id, login: '', displayName: `User #${id}`, roleName: '', groupId: null, groupName: '' };
  }

  private placeholderClient(id: number): AccessGroupClientOption {
    return { id, name: `บริษัท #${id}`, taxId: '', contact: '', telephone: '' };
  }

  private validateStep(step: number, showToast: boolean): boolean {
    if (step === 1) {
      if (!this.form.siteId) {
        if (showToast) this.toast.error('กรุณาเลือกลานจากแถบด้านข้าง');
        return false;
      }
      if (!this.form.groupType) {
        if (showToast) this.toast.error('กรุณาเลือกหมวดหมู่กลุ่ม');
        return false;
      }
      if (!this.form.name?.trim()) {
        if (showToast) this.toast.error('กรุณาระบุชื่อกลุ่ม');
        return false;
      }
    }
    if (step === 2 && this.clientScopeMode === 'specific' && !this.form.clientIds.length) {
      if (showToast) this.toast.error('เลือกบริษัทผู้เช่าอย่างน้อย 1 บริษัท หรือเลือก "ทุกบริษัทในลาน"');
      return false;
    }
    if (step === 3 && !this.form.resUserIds.length) {
      if (showToast) this.toast.error('เลือกสมาชิกอย่างน้อย 1 คน');
      return false;
    }
    if (step === 5 && !this.form.subscriptionIds.length) {
      if (showToast) this.toast.error('เลือก Subscription จอดฟรีอย่างน้อย 1 รายการ');
      return false;
    }
    return true;
  }

  private validateAll(showToast: boolean): boolean {
    if (!this.canUpdate) {
      if (showToast) this.toast.error('ไม่มีสิทธิ์บันทึก');
      return false;
    }
    for (const step of [1, 2, 3, 5]) {
      if (!this.validateStep(step, showToast)) {
        this.setStep(step);
        return false;
      }
    }
    return true;
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  // ================= API Functions =================

  private loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) return;
    this.isLoading.set(true);
    this.api.getAccessGroups(siteId).subscribe({
      next: (res) => {
        console.log('getAccessGroups', res);
        this.items.set(mapAccessGroupList(res));
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดกลุ่มสิทธิ์ล้มเหลว');
      },
    });
  }

  private loadMembers(groupId: number): void {
    this.isMembersLoading.set(true);
    this.api.getAccessGroupMembers(groupId).subscribe({
      next: (res) => {
        console.log('getAccessGroupMembers', res);
        const members = mapAccessGroupMembers(res);
        for (const u of members.users) this.userCache.set(u.id, u);
        for (const c of members.clients) this.clientCache.set(c.id, c);
        this.isMembersLoading.set(false);
      },
      error: () => {
        this.isMembersLoading.set(false);
        this.toast.error('โหลดรายชื่อสมาชิกของกลุ่มล้มเหลว');
      },
    });
  }

  private loadUserOptions(): void {
    const siteId = this.form.siteId || this.selectedSiteId;
    if (!siteId) return;
    const seq = ++this.userRequestSeq;
    this.isUserLoading.set(true);
    this.api
      .getAccessGroupUserOptions({
        siteId,
        search: this.userSearch,
        hideAssigned: this.userHideAssigned,
        currentGroupId: this.form.id,
        page: this.userPage(),
        pageSize: PICKER_PAGE_SIZE,
      })
      .subscribe({
        next: (res) => {
          console.log('getAccessGroupUserOptions', res);
          if (seq !== this.userRequestSeq) return;
          const page = mapAccessGroupOptionPage(res, mapAccessGroupUserOption);
          this.userOptions.set(page.data);
          this.userTotal.set(page.total);
          this.userTotalPages.set(page.totalPages);
          this.isUserLoading.set(false);
        },
        error: () => {
          if (seq !== this.userRequestSeq) return;
          this.userOptions.set([]);
          this.isUserLoading.set(false);
          this.toast.error('โหลดรายชื่อผู้ใช้ล้มเหลว');
        },
      });
  }

  private loadClientOptions(): void {
    const siteId = this.form.siteId || this.selectedSiteId;
    if (!siteId) return;
    const seq = ++this.clientRequestSeq;
    this.isClientLoading.set(true);
    this.api
      .getAccessGroupClientOptions({
        siteId,
        search: this.clientSearch,
        page: this.clientPage(),
        pageSize: PICKER_PAGE_SIZE,
      })
      .subscribe({
        next: (res) => {
          console.log('getAccessGroupClientOptions', res);
          if (seq !== this.clientRequestSeq) return;
          const page = mapAccessGroupOptionPage(res, mapAccessGroupClientOption);
          this.clientOptions.set(page.data);
          this.clientTotal.set(page.total);
          this.clientTotalPages.set(page.totalPages);
          this.isClientLoading.set(false);
        },
        error: () => {
          if (seq !== this.clientRequestSeq) return;
          this.clientOptions.set([]);
          this.isClientLoading.set(false);
          this.toast.error('โหลดรายชื่อบริษัทผู้เช่าล้มเหลว');
        },
      });
  }

  private loadSubscriptions(siteId: number): void {
    if (!siteId) {
      this.subscriptions.set([]);
      return;
    }
    this.api.getFreeParkingSubscriptions(siteId).subscribe({
      next: (res) => this.subscriptions.set(mapFreeParkingSubscriptionList(res)),
      error: () => this.subscriptions.set([]),
    });
  }
}
